<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="94%"
    top="14vh"
    custom-class="monitor-detail-dialog"
    :close-on-click-modal="false"
    append-to-body
  >
    <div slot="title" class="detail-heading">
      <h3>{{ config.title }}</h3>
      <p>UID：{{ uid || "--" }} <i /> 来源字段：{{ config.source }}</p>
    </div>

    <div class="precision-tip">金额直接截取保留 8 位小数</div>
    <el-table
      v-loading="loading"
      :data="pagedItems"
      class="detail-table"
      max-height="510"
      element-loading-text="数据加载中..."
      :empty-text="loading ? ' ' : '暂无数据'"
    >
      <el-table-column
        v-for="column in config.columns"
        :key="column.prop"
        :prop="column.prop"
        :label="column.label"
        :min-width="column.minWidth || 140"
      >
        <template slot-scope="scope">
          <span :class="valueClass(scope.row[column.prop], column)">
            {{ formatValue(scope.row[column.prop], column) }}
          </span>
        </template>
      </el-table-column>
    </el-table>
    <p v-if="config.tip" class="detail-tip">{{ config.tip }}</p>

    <div slot="footer" class="detail-footer">
      <span v-if="loading">加载中...</span>
      <span v-else>共 {{ items.length }} 条</span>
      <div class="footer-actions">
        <el-pagination
          v-if="items.length > pageSize"
          small
          layout="prev, pager, next"
          :current-page.sync="page"
          :page-size="pageSize"
          :total="items.length"
        />
        <el-button type="primary" @click="dialogVisible = false"
          >关闭</el-button
        >
      </div>
    </div>
  </el-dialog>
</template>

<script>
import Decimal from "decimal.js";

function formatDateTime(value) {
  const text = String(value);
  if (/^\d+$/.test(text)) {
    const date = new Date(Number(text));
    if (!Number.isNaN(date.getTime())) {
      const dateParts = [
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
      ];
      const timeParts = [date.getHours(), date.getMinutes(), date.getSeconds()];
      const dateText = dateParts
        .map((item, index) => String(item).padStart(index ? 2 : 4, "0"))
        .join("-");
      const timeText = timeParts
        .map((item) => String(item).padStart(2, "0"))
        .join(":");
      return `${dateText} ${timeText}`;
    }
  }
  return text.replace("T", " ").slice(0, 19);
}

export default {
  name: "MonitorDetailDialog",
  props: {
    visible: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    uid: { type: [String, Number], default: "" },
    config: {
      type: Object,
      default: () => ({ title: "", source: "", columns: [] }),
    },
    items: { type: Array, default: () => [] },
  },
  data() {
    return {
      page: 1,
      pageSize: 10,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    pagedItems() {
      const start = (this.page - 1) * this.pageSize;
      return this.items.slice(start, start + this.pageSize);
    },
  },
  watch: {
    visible(value) {
      if (value) this.page = 1;
    },
  },
  methods: {
    formatValue(value, column) {
      if (value === null || value === undefined || value === "") return "--";
      if (column.type === "amount") {
        return new Decimal(value)
          .toDecimalPlaces(8, Decimal.ROUND_DOWN)
          .toString();
      }
      if (column.type === "datetime") return formatDateTime(value);
      if (column.type === "depositType") {
        if (Number(value) === 1) return "链上充值";
        if (Number(value) === -2) return "内部转入";
        return value;
      }
      if (column.type === "withdrawType") {
        if (Number(value) === -1) return "链上提现";
        if (Number(value) === -2) return "内部转出";
        return value;
      }
      if (column.type === "ratio") {
        const ratio = new Decimal(value);
        const percent = ratio.lte(1) ? ratio.mul(100) : ratio;
        return `${percent
          .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
          .toString()}%`;
      }
      if (column.type === "rate") return `${value}%`;
      return value;
    },
    valueClass(value, column) {
      if (column.type !== "amount" || value === null || value === undefined)
        return "";
      return Number(value) < 0 ? "amount-negative" : "";
    },
  },
};
</script>

<style lang="less" scoped>
.detail-heading {
  h3 {
    margin: 0;
    color: #1f2a3d;
    font-size: 24px;
    line-height: 34px;
  }

  p {
    display: flex;
    align-items: center;
    margin: 8px 0 0;
    color: #738096;
    font-size: 13px;
  }

  i {
    width: 1px;
    height: 13px;
    margin: 0 18px;
    background: #8792a6;
  }
}

.precision-tip {
  margin-bottom: 15px;
  color: #8a96aa;
  font-size: 12px;
  text-align: right;
}

.detail-tip {
  margin: 24px 0 0;
  color: #68758a;
  font-size: 12px;
}

.amount-negative {
  color: #ff5a5f;
}

.detail-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  color: #5f6b7e;
}

.footer-actions {
  display: flex;
  align-items: center;
  gap: 28px;
}

::v-deep .monitor-detail-dialog {
  display: flex;
  flex-direction: column;
  height: 76vh;
  max-width: 1422px;
  max-height: 800px;
  margin-bottom: 0;
  border-radius: 16px;
  box-shadow: 10px 14px 0 rgba(17, 30, 52, 0.92);

  .el-dialog__header {
    flex: none;
    padding: 23px 34px 14px;
    border-bottom: 1px solid #e4e8ef;
  }

  .el-dialog__headerbtn {
    top: 27px;
    right: 28px;
    font-size: 26px;
  }

  .el-dialog__body {
    flex: 1;
    padding: 17px 34px 20px;
    overflow: auto;
  }

  .el-dialog__footer {
    flex: none;
    padding: 14px 34px;
    border-top: 1px solid #e4e8ef;
  }

  .el-table::before {
    display: none;
  }

  .el-table th {
    height: 55px;
    color: #40506a;
    background: #f7f9fc;
  }

  .el-table td {
    height: 59px;
    border-bottom-color: #e1e6ee;
  }

  .el-button {
    min-width: 79px;
  }
}
</style>
